import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {KmOrMiles} from '../enum/km-miles.enum';
import {CommentPayload} from './payloads/comment.payload';
import {TimelinePayload} from './payloads/timeline.payload';
import {RecordedRoutePayload} from './payloads/recorded-route.payload';
import {CommentList} from './resources/comment-list.resource';
import {RecordedRouteResource} from './resources/recorded-route.resource';
import {RoadtripDetail} from './resources/roadtrip-detail.resource';
import {RoadtripList} from './resources/roadtrip-list.resource';
import {RoadtripMapItem} from './resources/roadtrip-map-item.resource';
import {UnrUser} from './resources/unr-user.resource';
import {UserRegistration} from './resources/user-registration.resource';

@Injectable({
  providedIn: 'root'
})
export class UnrApiService {

  private readonly baseUrl: string = '';

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.apiUrl;
  }

  getRecordedRoutes(uid: string, kmOrMiles: KmOrMiles = KmOrMiles.KM): Observable<Array<RecordedRouteResource>> {
    return this.httpClient
      .get<{ userRoutes: Array<RecordedRouteResource> }>(`${this.baseUrl}/v1/routes/${uid}`, {
        params: new HttpParams().set('kmOrMiles', kmOrMiles)
      })
      .pipe(
        map(response => response.userRoutes)
      );
  }
  
  uploadImage(image: Blob, firebaseUid: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', image, `${firebaseUid} - ${Date.now()}.jpg`);
    const headers = {enctype: 'multipart/form-data'};

    return this.httpClient
      .post(
        `${this.baseUrl}/user/v1/storage/uploadUserImage`,
        formData,
        {
          headers,
          responseType: 'text',
          observe: 'response'
        }
      )
  }

  uploadUserRoute(file: Blob, time: string, date, distance, firebaseUid: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, new Date().toISOString() + '.gpx');
    const headers = {enctype: 'multipart/form-data'};
    console.log({time, date, distance}, 'save');
    return this.httpClient
      .post(
        `${this.baseUrl}/user/v1/storage/uploadUserRoute`,
        formData,
        {
          headers,
          responseType: 'text',
          observe: 'response'
        },
      ).pipe(
        switchMap((response: HttpResponse<string>) => this.postUserRoute({
          date,
          time,
          distance,
          gpxFileLocation: response.body,
          firebaseUid
        }))
      );
  }

  postUserRoute(payload: RecordedRoutePayload): Observable<void> {
    return this.httpClient.post<void>(`${this.baseUrl}/v1/routes`, payload);
  }

  getUserdetails(uid: string, kmOrMiles: string): Observable<UnrUser> {
    return this.httpClient.get<UnrUser>(`${this.baseUrl}/v1/users/${uid}?kmOrMiles=${kmOrMiles}`);
  }

  getMapCoordsForAllRoadtrips(): Observable<RoadtripMapItem[]> {
    return this.httpClient.get<{ roadTrips: RoadtripMapItem[] }>(`${this.baseUrl}/v1/roadtrips/map`)
      .pipe(
        map(response => response.roadTrips)
      );
  }

  getCommentsForRoadTrip(roadtripId: string): Observable<CommentList> {
    return this.httpClient.get<CommentList>(`${this.baseUrl}/v1/roadtrips/${roadtripId}/comments`);
  }

  postCommentAndRatingForRoadtrip(roadtripId: number, payload: CommentPayload): Observable<object> {
    return this.httpClient.post(`${this.baseUrl}/v1/roadtrips/${roadtripId}/comment`, payload);
  }

  getCommentsForStep(stepId: string): Observable<CommentList> {
    return this.httpClient.get<CommentList>(`${this.baseUrl}/v1/steps/${stepId}/comments`);
  }

  postCommentAndRatingForStep(stepId: number, payload: CommentPayload): Observable<object> {
    return this.httpClient.post(`${this.baseUrl}/v1/steps/${stepId}/comment`, payload);
  }
  postTimeline(payload: TimelinePayload): Observable<object> {
    return this.httpClient.post(`${this.baseUrl}/v1/timeline`, payload);
  }
  getTimeline(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/v1/timeline`);
  }
  getRoadTripList(kmOrMiles: string): Observable<RoadtripList> {
    return this.httpClient.get<RoadtripList>(`${this.baseUrl}/v1/roadtrips?kmOrMiles=${kmOrMiles}`);
  }

  getRoadTripDetails(id: string, kmOrMiles: string = 'km'): Observable<RoadtripDetail> {
    
    return this.httpClient.get<RoadtripDetail>(`${this.baseUrl}/v1/roadtrips/${id}?kmOrMiles=${kmOrMiles}`);
  }

  registerUser(email?: string): Observable<UserRegistration> {
    const headers = {'Content-type': 'application/json'};
    const payload: { email: string } = {email};

    return this.httpClient.post<UserRegistration>(
      `${this.baseUrl}/v1/users`,
      email ? payload : undefined,
      {headers}
    );
  }

  saveSettings(payload: any): Observable<any> {
    return this.httpClient.post(
      `${this.baseUrl}/v1/users/settings`,
      payload
    );
  }

  // TODO: Combine with saveSettings
  // https://gitlab.com/bart-blommaerts/unrestricted-app/-/issues/52#note_339423675
  saveAnySetting(payload: any): Observable<any> {
    return this.httpClient.post(
      `${this.baseUrl}/v1/users/setting/existinguser`,
      payload
    );
  }
}
